import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import React, { useState, useEffect, useRef } from "react";

import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import { Box } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import Title from "../../../common/Title";
import InputFieldBox from "../../../common/InputFieldBox";

import useAlert from "../../../../hooks/useAlert";
import useAxios from "../../../../hooks/useAxios";
import { __activateTmobilej } from "../../../../api/sim";

const TmobilejActivateForm = () => {
  const [formData, setFormData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const responseRef = useRef(null);

  const { setAlertMessage } = useAlert();
  const [response, call, loading] = useAxios({
    ...__activateTmobilej,
  });
  const nav = useNavigate();

  const initialData = {
    requested_lines: 0,
    areacode: "",
    zipcode: "",
    skip_zip_checks: false,
  };

  const validationSchema = yup
    .object({
      requested_lines: yup
        .number("requested_lines")
        .required("Requested lines is required")
        .min(1, "Requested lines must be at least 1")
        .integer("Requested lines must be a whole number"),
      skip_zip_checks: yup.boolean("skip_zip_checks").required(),
      areacode: yup
        .string("areacode")
        .test("Digits only", "The field should have 3 digits only", (value) =>
          value && value.length ? /^\d{3}$/.test(value) : true
        )
        .optional(),
      zipcode: yup
        .string("zipcode")
        .test("Digits only", "The field should have 5 digits only", (value) =>
          value && value.length ? /^\d{5}$/.test(value) : true
        )
        .optional(),
    })
    .test(
      "at-least-one-location",
      "Either area code or zip code is required",
      function (values) {
        const { areacode, zipcode } = values;
        return (
          (areacode && areacode.trim() !== "") ||
          (zipcode && zipcode.trim() !== "")
        );
      }
    );

  const formik = useFormik({
    initialValues: { ...initialData },
    onSubmit: (values) => {
      const requestData = Object.keys(values).reduce((obj, key) => {
        obj[key] = values[key];
        return obj;
      }, {});

      setFormData(requestData);
    },
    validationSchema: validationSchema,
    validateOnChange: true,
  });

  useEffect(() => {
    if (formData && !isSubmitting) {
      setIsSubmitting(true);
      formik.setSubmitting(true);
      call({
        data: {
          skip_zip_checks: formData.skip_zip_checks,
          areacode: formData.areacode,
          zipcode: formData.zipcode,
          requested_lines: formData.requested_lines,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, call, isSubmitting]);

  useEffect(() => {
    if (!response || !isSubmitting) return;
    
    // Check if this is a new response (different from the last processed one)
    if (responseRef.current && 
        responseRef.current.status === response.status && 
        JSON.stringify(responseRef.current.data) === JSON.stringify(response.data)) {
      return;
    }

    if (response.status) {
      setAlertMessage({ type: "success", message: "Activation queue started" });
      nav("/tmobilej/activate");
    }

    // Store this response as the last processed one
    responseRef.current = response;
    
    // Reset states after processing response
    formik.setSubmitting(false);
    setIsSubmitting(false);
    setFormData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, nav, setAlertMessage, isSubmitting]);

  if (loading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content="Activate new sims"
          sx={{ mb: 3 }}
          gutterBottom
        />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.requested_lines) ? true : false}
              sx={{ width: "300px" }}
            >
              <InputFieldBox
                value={formik.values.requested_lines}
                onChange={(event) =>
                  formik.setFieldValue("requested_lines", event.target.value)
                }
                label="Requested Lines"
                type="number"
                placeholder="Enter number of lines to activate"
              />

              {Boolean(formik.errors.requested_lines) ? (
                <FormHelperText id="requested_lines-error-text">
                  {formik.errors.requested_lines}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.areacode) ? true : false}
              sx={{ width: "300px" }}
            >
              <FormHelperText sx={{ mb: 1, color: "text.secondary" }}>
                At least one is required (Area Code or Zip Code)
              </FormHelperText>
              <InputFieldBox
                value={formik.values.areacode}
                onChange={(event) =>
                  formik.setFieldValue("areacode", event.target.value)
                }
                label="Area Code"
                type="text"
                placeholder="Enter 3-digit area code"
                maxLength={3}
              />

              {Boolean(formik.errors.areacode) ? (
                <FormHelperText id="areacode-error-text">
                  {formik.errors.areacode}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.zipcode) ? true : false}
              sx={{ width: "300px" }}
            >
              <InputFieldBox
                value={formik.values.zipcode}
                onChange={(event) =>
                  formik.setFieldValue("zipcode", event.target.value)
                }
                label="Zip Code"
                type="text"
                placeholder="Enter 5-digit zip code"
                maxLength={5}
              />

              {Boolean(formik.errors.zipcode) ? (
                <FormHelperText id="zipcode-error-text">
                  {formik.errors.zipcode}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.skip_zip_checks) ? true : false}
              sx={{ width: "300px" }}
            >
              <InputFieldBox
                isCheckbox={true}
                value={formik.values.skip_zip_checks}
                onChange={(event) =>
                  formik.setFieldValue("skip_zip_checks", event.target.checked)
                }
                label="Skip Zip checks"
                disabled={!formik.values.areacode && !formik.values.zipcode}
                sx={{
                  opacity:
                    !formik.values.areacode && !formik.values.zipcode ? 0.5 : 1,
                }}
              />

              {Boolean(formik.errors.skip_zip_checks) ? (
                <FormHelperText id="skip_zip_checks-error-text">
                  {formik.errors.skip_zip_checks}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          {Boolean(formik.errors["at-least-one-location"]) && (
            <Grid item xs={12}>
              <FormControl error={true} sx={{ width: "300px" }}>
                <FormHelperText id="location-error-text">
                  {formik.errors["at-least-one-location"]}
                </FormHelperText>
              </FormControl>
            </Grid>
          )}

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={
                  formik.errors.requested_lines ||
                  !formik.values.requested_lines ||
                  formik.errors.areacode ||
                  formik.errors.zipcode ||
                  formik.errors["at-least-one-location"] ||
                  (!formik.values.areacode && !formik.values.zipcode) ||
                  formik.isSubmitting
                }
                onClick={() =>
                  !loading && !formik.isSubmitting && formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {"Activate new SIMs"}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default TmobilejActivateForm;
